import { HardHat } from 'lucide-react';
import type { VerticalContent } from '../VerticalLandingPage';

export const constructionContent: VerticalContent = {
  vertical: 'construction',
  industryLabel: 'Construction',
  heroIcon: HardHat,
  heroBadge: 'For GCs, subs, and site PMs',
  heroHeadline: 'Run every jobsite from one place. Without losing the daily field detail.',
  heroSub:
    'Karyva tracks submittals, RFIs, inspections, and weather delays the way construction teams actually work — with photo evidence, sign-offs, and crew-level visibility your superintendents will actually use on a phone.',
  trustStats: [
    { value: 'Photo', label: 'evidence on every closeout' },
    { value: 'Site-level', label: 'access control per PM' },
    { value: '14-day', label: 'free trial, no card needed' },
  ],
  pains: [
    {
      title: 'Submittals and RFIs live in everyone\'s inbox',
      body:
        'Email threads, PDFs in three Google Drives, a binder in the trailer. When a wall opens up wrong, nobody can find the approved submittal in under 20 minutes.',
    },
    {
      title: 'The field works on paper, the office on spreadsheets',
      body:
        'Daily logs, punch lists, and inspection sign-offs get re-typed at the end of the day — if at all. By the time the PM sees yesterday\'s rebar issue, the slab is already poured.',
    },
    {
      title: 'Weather and inspector delays cascade silently',
      body:
        'A failed inspection on Tuesday slides framing into the following week, which pushes drywall, which pushes the punch walk. The schedule says "on track" until the GC owes liquidated damages.',
    },
    {
      title: 'Subs see things they shouldn\'t — or can\'t see what they need',
      body:
        'Your electrical sub doesn\'t need to see the mechanical contract value, and your owner shouldn\'t see your margin on change orders. Most PM tools give them everything or nothing.',
    },
  ],
  bridges: [
    {
      painHook: 'Submittals scattered across email and Drive',
      feature: 'Submittal log with photo + PDF evidence on every status',
      body:
        'Every submittal is a task with its own approval gate. Architect approves with a digital signature, photo of stamped drawings attached, status flips to Approved automatically. Searchable from the trailer or the truck.',
    },
    {
      painHook: 'Daily field reports never make it to the office',
      feature: 'Mobile-first daily log with offline photo capture',
      body:
        'Foremen log crew, weather, deliveries, and incidents on a phone. Photos timestamp and geotag automatically. The PM sees yesterday\'s logs over morning coffee — not at the weekly OAC.',
    },
    {
      painHook: 'Schedule lies until the damages letter shows up',
      feature: 'Inspection + weather-delay automation rolls forward dependent tasks',
      body:
        'Mark an inspection Failed or a day Weather Delay and dependent tasks reschedule with you, with the float burndown visible. The GC sees real risk by 7am, not on the day completion is due.',
    },
    {
      painHook: 'Wrong people see wrong numbers',
      feature: 'Site-level access control plus field-level budget hiding',
      body:
        'Each project is its own access boundary. Subs see only their scope. Owners see commitments, not your margin. Junior coordinators see schedule but not financials. Configurable, audited, baked in.',
    },
  ],
  workflowTitle: 'A week on the jobsite, in Karyva',
  workflowSteps: [
    {
      when: 'Monday morning',
      what:
        'Superintendent posts the weekly look-ahead from the trailer. Tasks auto-distribute to each sub\'s mobile app. Owner sees a clean schedule view; subs see only their lines.',
    },
    {
      when: 'Wednesday',
      what:
        'Inspector fails the rough-in. The foreman flips the inspection task to Failed with a photo. Karyva auto-shifts drywall start, notifies the framing sub of rework, logs the delay reason for the change-order packet.',
    },
    {
      when: 'Friday',
      what:
        'Weekly OAC meeting opens with the live dashboard: % complete by trade, RFI cycle times, open submittals over 5 days. No "let me get back to you on that" from the GC.',
    },
    {
      when: 'Closeout',
      what:
        'Every punch item closes with a photo and a sign-off. The closeout binder generates itself — warranties, as-builts, O&M manuals — and ships to the owner the day of substantial completion.',
    },
  ],
  whyKaryva: [
    'Submittals, RFIs, inspections, punch lists — not "Sprints" and "Epics" pretending to be them.',
    'Photo evidence and digital sign-offs on every status change. Audit-ready by default.',
    'Mobile-first for the field, browser for the office. Same data, same source of truth.',
    'Per-site access control so subs, owners, and lenders see only what they should.',
  ],
  testimonial: {
    quote:
      'Our PMs used to spend Friday afternoons rebuilding the schedule from emails. Now they spend it walking the site.',
    name: 'Construction PM (pilot customer)',
    role: 'Mid-Atlantic GC — 12 active projects',
  },
  logos: ['BuildCo', 'Northstar GC', 'Cornerstone', 'Apex Builders'],
  faqs: [
    {
      q: 'Do my field crews need an account?',
      a: 'No. Foremen and crew can log daily reports and sign off on tasks from a phone link without a paid seat — you pay only for office users (PMs, supers, coordinators).',
    },
    {
      q: 'Can I keep my existing schedule tool (Primavera, MS Project)?',
      a: 'Yes. Karyva imports baseline schedules and pushes daily progress back. We don\'t try to replace Primavera — we replace the spreadsheets and email threads that orbit it.',
    },
    {
      q: 'How do you handle sub access to drawings and contract docs?',
      a: 'Each sub gets a guest portal scoped to their scope of work. They see drawings, RFIs, and submittals relevant to their trade — never your contract value, change-order margin, or owner correspondence.',
    },
    {
      q: 'Is this only for GCs, or does it work for owners and developers too?',
      a: 'Both. Owners use Karyva to oversee a portfolio of active builds without ever logging into the GC\'s system. We give the owner the dashboard view; the GC keeps full control of the project workspace.',
    },
  ],
};
